import { zodResolver } from '@hookform/resolvers/zod';
import {
  configMgmtControllerAddFeeComponentToStudyType,
  configMgmtControllerCreateRequiredStudyType,
  configMgmtControllerCreateStudyType,
  configMgmtControllerCreateTahseelStudyRequestType,
  configMgmtControllerDeleteRequiredStudyType,
  configMgmtControllerDeleteStudyType,
  configMgmtControllerDeleteTahseelStudyRequestType,
  configMgmtControllerListFeeComponentTypes,
  configMgmtControllerListRequiredStudyTypes,
  configMgmtControllerListStudyTypes,
  configMgmtControllerListTahseelStudyRequestTypes,
  configMgmtControllerRemoveStudyTypeFeeComponent,
  configMgmtControllerUpdateRequiredStudyType,
  configMgmtControllerUpdateStudyType,
  configMgmtControllerUpdateTahseelStudyRequestType,
  pledgeStudyConfigControllerCreateMeasurementPoint,
  pledgeStudyConfigControllerListMeasurementPoints,
  pledgeStudyConfigControllerUpdateMeasurementPoint,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { BookOpen, Coins, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormField } from '../../components/forms/form-field';
import { AppLayout } from '../../components/layout/app-layout';
import { PageHeader } from '../../components/layout/page-header';
import { Button } from '../../components/ui/button';
import { DataTable, type DataTableColumn } from '../../components/ui/data-table';
import { Dialog, DialogContent, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Price } from '../../components/ui/price';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { usePermissions } from '../../hooks/use-permissions';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';
import { usePledgeStudyConfigStore } from '../../stores/config/pledge-study-config.store';

const studyTypeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z.string().min(1, 'Code is required'),
  status: z.enum(['ACTIVE', 'INACTIVE']),
});

const requiredStudyTypeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z.string().min(1, 'Code is required'),
  status: z.enum(['ACTIVE', 'INACTIVE']),
});

const tahseelStudyRequestTypeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z.string().min(1, 'Code is required'),
  status: z.enum(['ACTIVE', 'INACTIVE']),
});

const measurementPointSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z.string().min(1, 'Code is required'),
  unitPrice: z
    .string()
    .min(1)
    .refine((v) => !Number.isNaN(Number(v)) && Number(v) >= 0, 'Must be a valid price'),
  status: z.enum(['ACTIVE', 'INACTIVE']),
});

const feeLineSchema = z.object({
  typeId: z.string().min(1, 'Select fee type'),
  amount: z
    .string()
    .min(1)
    .refine((v) => Number(v) > 0, 'Amount must be > 0'),
  payee: z.string().optional(),
});

export const Route = createFileRoute('/config/pledge-study')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('config:view')) throw redirect({ to: '/dashboard' });
  },
  component: PledgeStudyConfigPage,
});

interface MasterFeeType {
  id: string;
  name: string;
  code: string;
  status: string;
}

interface StudyTypeFeeComponent {
  id: string;
  feeComponentTypeId: string;
  feeComponentType: { id: string; name: string; code: string };
  payee?: string | null;
  amount: string;
}

interface StudyType {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
  status: string;
  feeComponents: StudyTypeFeeComponent[];
  totalFee: string;
}

interface RequiredStudyType {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
  status: string;
}

interface TahseelStudyRequestType {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
  status: string;
}

interface FeeLine {
  feeComponentTypeId: string;
  feeTypeName: string;
  feeTypeCode: string;
  amount: string;
  payee: string;
}

interface MeasurementPointType {
  id: string;
  name: string;
  code: string;
  unitPrice: string;
  previousUnitPrice?: string | null;
  status: string;
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold',
        status === 'ACTIVE'
          ? 'bg-success-50 text-success-700 border border-success-200'
          : 'bg-gray-100 text-gray-500 border border-gray-200',
      )}
    >
      {status === 'ACTIVE' ? 'Active' : 'Inactive'}
    </span>
  );
}

function SectionCard({
  title,
  subtitle,
  headerAction,
  children,
}: {
  title: string;
  subtitle?: string;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-gray-400">{subtitle}</p>}
        </div>
        {headerAction}
      </div>
      {children}
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: { children: React.ReactNode; align?: 'left' | 'right' | 'center' }) {
  return (
    <th
      className={cn(
        'px-6 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
  className,
}: { children: React.ReactNode; align?: 'left' | 'right' | 'center'; className?: string }) {
  return (
    <td
      className={cn(
        'px-6 py-3.5 text-sm',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {children}
    </td>
  );
}

// ── Study Types ───────────────────────────────────────────────────────────────

function generateStudyCode(name: string): string {
  return (
    name
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_|_$/g, '') || 'STUDY'
  );
}

function FeeLineCard({
  name,
  code,
  payee,
  amount,
  onRemove,
}: {
  name: string;
  code: string;
  payee?: string | null;
  amount: string | number;
  onRemove?: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50">
        <Coins size={16} className="text-emerald-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="inline-block rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] text-gray-500">
            {code}
          </span>
          {payee && <span className="text-[11px] text-gray-400">→ {payee}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Price
          value={amount}
          className="text-sm font-semibold text-gray-800"
          iconClassName="text-gray-500 w-[0.85em] h-[0.85em]"
        />
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function AddFeeForm({
  availableTypes,
  activeTypes,
  onAdd,
  onCancel,
}: {
  availableTypes: MasterFeeType[];
  activeTypes: MasterFeeType[];
  onAdd: (typeId: string, amount: string, payee: string) => void;
  onCancel: () => void;
}) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<z.infer<typeof feeLineSchema>>({
    resolver: zodResolver(feeLineSchema),
    defaultValues: { typeId: '', amount: '', payee: '' },
  });
  return (
    <form
      onSubmit={handleSubmit((d) => onAdd(d.typeId, d.amount, d.payee ?? ''))}
      className="rounded-xl border border-brand-200 bg-brand-50/40 px-4 py-4 space-y-3"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
        Add Fee Component
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <FormField label="Fee Component Type" error={errors.typeId?.message}>
            <Controller
              name="typeId"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="h-9 border-gray-300 text-xs rounded-xl">
                    <SelectValue placeholder="Select type…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTypes.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-gray-400">
                        {activeTypes.length === 0
                          ? 'No fee component types defined yet.'
                          : 'All active types already added.'}
                      </div>
                    ) : (
                      availableTypes.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          <span className="font-medium">{t.name}</span>
                          <span className="ml-2 font-mono text-[10px] text-gray-400">{t.code}</span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            />
          </FormField>
        </div>
        <FormField label="Payee">
          <Input
            type="text"
            placeholder="e.g. Beeah, Municipality"
            className="h-8 py-1 text-xs"
            {...register('payee')}
          />
        </FormField>
        <FormField label="Amount" error={errors.amount?.message}>
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            className="h-8 py-1 text-xs"
            hasError={!!errors.amount}
            {...register('amount')}
          />
        </FormField>
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" type="submit">
          <Plus size={12} />
          Add
        </Button>
      </div>
    </form>
  );
}

function StudyTypeDialog({ studyType, onClose }: { studyType?: StudyType; onClose: () => void }) {
  const isEdit = !!studyType;
  const qc = useQueryClient();
  const {
    register: registerST,
    handleSubmit: handleSubmitST,
    watch: watchST,
    setValue: setValueST,
    control: controlST,
  } = useForm<z.infer<typeof studyTypeSchema>>({
    resolver: zodResolver(studyTypeSchema),
    defaultValues: {
      name: studyType?.name ?? '',
      code: studyType?.code ?? '',
      status: studyType?.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
    },
  });
  const [removedFeeIds, setRemovedFeeIds] = useState<Set<string>>(new Set());
  const [newFeeLines, setNewFeeLines] = useState<FeeLine[]>([]);
  const [showAddFeeForm, setShowAddFeeForm] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { data: masterTypes = [] } = useQuery<MasterFeeType[]>({
    queryKey: ['allFeeComponentTypes'],
    queryFn: () => configMgmtControllerListFeeComponentTypes() as Promise<MasterFeeType[]>,
  });
  const activeTypes = masterTypes.filter((t) => t.status === 'ACTIVE');
  const visibleExisting = (studyType?.feeComponents ?? []).filter(
    (fc) => !removedFeeIds.has(fc.id),
  );
  const assignedTypeIds = new Set([
    ...visibleExisting.map((fc) => fc.feeComponentTypeId),
    ...newFeeLines.map((fl) => fl.feeComponentTypeId),
  ]);
  const availableTypes = activeTypes.filter((t) => !assignedTypeIds.has(t.id));
  const totalFee =
    visibleExisting.reduce((s, fc) => s + Number(fc.amount), 0) +
    newFeeLines.reduce((s, fl) => s + Number(fl.amount), 0);
  const totalCount = visibleExisting.length + newFeeLines.length;

  function addFeeLine(typeId: string, amount: string, payee: string) {
    const type = activeTypes.find((t) => t.id === typeId);
    if (!type) return;
    setNewFeeLines((prev) => [
      ...prev,
      {
        feeComponentTypeId: type.id,
        feeTypeName: type.name,
        feeTypeCode: type.code,
        amount,
        payee,
      },
    ]);
    setShowAddFeeForm(false);
  }

  const onSave = handleSubmitST(async (data) => {
    setSaveError(null);
    setIsSaving(true);
    try {
      if (isEdit) {
        await configMgmtControllerUpdateStudyType({
          studyTypeId: studyType?.id,
          requestBody: { name: data.name.trim(), code: data.code.trim(), status: data.status },
        });
        for (const feeId of removedFeeIds) {
          await configMgmtControllerRemoveStudyTypeFeeComponent({
            studyTypeId: studyType?.id,
            linkId: feeId,
          });
        }
        for (const fl of newFeeLines) {
          await configMgmtControllerAddFeeComponentToStudyType({
            studyTypeId: studyType?.id,
            requestBody: {
              feeComponentTypeId: fl.feeComponentTypeId,
              amount: fl.amount,
              payee: fl.payee || undefined,
            },
          });
        }
      } else {
        const created = await configMgmtControllerCreateStudyType({
          requestBody: {
            name: data.name.trim(),
            code: data.code.trim(),
            displayOrder: 0,
            status: data.status,
          },
        });
        const newId = (created as { id: string }).id;
        for (const fl of newFeeLines) {
          await configMgmtControllerAddFeeComponentToStudyType({
            studyTypeId: newId,
            requestBody: {
              feeComponentTypeId: fl.feeComponentTypeId,
              amount: fl.amount,
              payee: fl.payee || undefined,
            },
          });
        }
      }
      qc.invalidateQueries({ queryKey: ['globalStudyTypes'] });
      onClose();
    } catch (err: unknown) {
      try {
        const body = (await (err as { response?: Response }).response?.json()) as
          | { message?: string }
          | undefined;
        setSaveError(body?.message ?? `Failed to ${isEdit ? 'update' : 'create'} study type`);
      } catch {
        setSaveError(`Failed to ${isEdit ? 'update' : 'create'} study type`);
      }
    } finally {
      setIsSaving(false);
    }
  });

  const saveLabel = isSaving
    ? 'Saving…'
    : isEdit
      ? 'Save Changes'
      : totalCount > 0
        ? `Create with ${totalCount} fee component${totalCount !== 1 ? 's' : ''}`
        : 'Create Study Type';

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isSaving) onClose();
      }}
    >
      <DialogContent className="max-w-2xl p-0 gap-0 sm:rounded-2xl overflow-hidden">
        <div className="border-b border-gray-100 px-6 py-5 pr-14">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? 'Edit Study Type' : 'New Study Type'}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            {isEdit
              ? 'Rename this study type and manage its fee components'
              : 'Define a study type and assign fee components with payees'}
          </p>
        </div>
        <div
          className="overflow-y-auto px-6 py-5 space-y-5"
          style={{ maxHeight: 'calc(100dvh - 220px)' }}
        >
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Name" required>
              <Input
                type="text"
                autoFocus
                placeholder="e.g. Structural Study, Environmental Study"
                {...registerST('name', {
                  onBlur: (e) => {
                    if (!isEdit && !watchST('code')) {
                      setValueST('code', generateStudyCode(e.target.value));
                    }
                  },
                })}
              />
            </FormField>
            <FormField label="Code" required>
              <Input
                type="text"
                placeholder="e.g. STRUCTURAL_STUDY"
                className="font-mono uppercase"
                {...registerST('code')}
              />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Status">
              <Controller
                name="status"
                control={controlST}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="h-10 border-gray-300 rounded-xl text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">Active</SelectItem>
                      <SelectItem value="INACTIVE">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </FormField>
          </div>
          <div className="border-t border-gray-100" />
          <div>
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Fee Components
                </p>
                <p className="mt-0.5 text-[11px] text-gray-400">
                  {totalCount > 0
                    ? `${totalCount} component${totalCount !== 1 ? 's' : ''} added`
                    : 'Add fee components with payee and amount for this study type'}
                </p>
              </div>
              {totalFee > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-1.5 shrink-0">
                  <Price
                    value={totalFee}
                    className="text-sm font-semibold text-emerald-700"
                    iconClassName="text-emerald-500 w-[0.85em] h-[0.85em]"
                  />
                </span>
              )}
            </div>
            {(visibleExisting.length > 0 || newFeeLines.length > 0) && (
              <div className="space-y-2 mb-2">
                {visibleExisting.map((fc) => (
                  <FeeLineCard
                    key={fc.id}
                    name={fc.feeComponentType.name}
                    code={fc.feeComponentType.code}
                    payee={fc.payee}
                    amount={fc.amount}
                    onRemove={() => setRemovedFeeIds((prev) => new Set([...prev, fc.id]))}
                  />
                ))}
                {newFeeLines.map((fl) => (
                  <FeeLineCard
                    key={fl.feeComponentTypeId}
                    name={fl.feeTypeName}
                    code={fl.feeTypeCode}
                    payee={fl.payee}
                    amount={fl.amount}
                    onRemove={() =>
                      setNewFeeLines((prev) =>
                        prev.filter((x) => x.feeComponentTypeId !== fl.feeComponentTypeId),
                      )
                    }
                  />
                ))}
              </div>
            )}
            {showAddFeeForm ? (
              <AddFeeForm
                availableTypes={availableTypes}
                activeTypes={activeTypes}
                onAdd={addFeeLine}
                onCancel={() => setShowAddFeeForm(false)}
              />
            ) : (
              <button
                type="button"
                onClick={() => setShowAddFeeForm(true)}
                disabled={availableTypes.length === 0}
                className={cn(
                  'flex w-full items-center justify-center gap-2 rounded-xl border border-dashed py-3 text-sm font-medium transition-colors',
                  availableTypes.length === 0
                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 bg-white text-gray-500 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-600',
                )}
              >
                <Plus size={15} />
                {availableTypes.length === 0 && activeTypes.length > 0
                  ? 'All fee component types added'
                  : 'Add Fee Component'}
              </button>
            )}
          </div>
          {saveError && (
            <div className="rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600">
              {saveError}
            </div>
          )}
        </div>
        <DialogFooter className="border-t border-gray-100 px-6 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={onSave}
            disabled={isSaving || !watchST('name')?.trim()}
          >
            {saveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StudyTypesSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { data: studyTypes = [], isLoading } = useQuery<StudyType[]>({
    queryKey: ['globalStudyTypes'],
    queryFn: () => configMgmtControllerListStudyTypes() as unknown as Promise<StudyType[]>,
  });

  const {
    showCreate,
    editStudyType: _editStudyType,
    deleteConfirm,
    setShowCreate,
    setEditStudyType,
    setDeleteConfirm,
  } = usePledgeStudyConfigStore();
  const editStudyType = _editStudyType as StudyType | null;

  const deleteMutation = useMutation({
    mutationFn: (studyTypeId: string) => configMgmtControllerDeleteStudyType({ studyTypeId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['globalStudyTypes'] });
      setDeleteConfirm(null);
    },
  });

  const columns: DataTableColumn<StudyType>[] = [
    {
      key: 'code',
      header: 'Code',
      render: (s) => (
        <span className="rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-xs font-semibold text-gray-600">
          {s.code}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (s) => <span className="font-medium text-gray-900">{s.name}</span>,
    },
    {
      key: 'totalFee',
      header: 'Total Fee',
      align: 'right',
      render: (s) =>
        Number(s.totalFee ?? 0) > 0 ? (
          <Price
            value={s.totalFee}
            className="text-xs font-semibold text-gray-800"
            iconClassName="text-gray-500 w-[0.85em] h-[0.85em]"
          />
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    {
      key: 'feeCount',
      header: 'Components',
      align: 'center',
      render: (s) => (
        <span className="inline-flex items-center justify-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
          {s.feeComponents?.length ?? 0}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (s) => (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
            s.status === 'ACTIVE'
              ? 'bg-success-50 text-success-700 border border-success-200'
              : 'bg-gray-100 text-gray-500 border border-gray-200',
          )}
        >
          {s.status === 'ACTIVE' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: '',
            render: (s: StudyType) =>
              deleteConfirm === s.id ? (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">Delete?</span>
                  <button
                    type="button"
                    onClick={() => deleteMutation.mutate(s.id)}
                    disabled={deleteMutation.isPending}
                    className="text-xs font-medium text-error-600"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(null)}
                    className="text-xs text-gray-500"
                  >
                    No
                  </button>
                </div>
              ) : (
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setEditStudyType(s)}
                    className="rounded px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(s.id)}
                    className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ),
          } satisfies DataTableColumn<StudyType>,
        ]
      : []),
  ];

  return (
    <>
      <DataTable
        title="Study Types"
        description="Define global study types with fee components and payees used in pledge studies."
        headerActions={
          canEdit ? (
            <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
              <Plus size={14} />
              New Study Type
            </Button>
          ) : null
        }
        columns={columns}
        data={isLoading ? [] : studyTypes}
        keyField="id"
        emptyMessage="No study types defined"
      />
      {showCreate && <StudyTypeDialog onClose={() => setShowCreate(false)} />}
      {editStudyType && (
        <StudyTypeDialog studyType={editStudyType} onClose={() => setEditStudyType(null)} />
      )}
    </>
  );
}

// ── Required Study Types ──────────────────────────────────────────────────────

function RequiredStudyTypeDialog({
  requiredStudyType,
  onClose,
}: {
  requiredStudyType?: RequiredStudyType;
  onClose: () => void;
}) {
  const isEdit = !!requiredStudyType;
  const qc = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register: registerRST,
    handleSubmit: handleSubmitRST,
    watch: watchRST,
    setValue: setValueRST,
    control: controlRST,
  } = useForm<z.infer<typeof requiredStudyTypeSchema>>({
    resolver: zodResolver(requiredStudyTypeSchema),
    defaultValues: {
      name: requiredStudyType?.name ?? '',
      code: requiredStudyType?.code ?? '',
      status: requiredStudyType?.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
    },
  });

  const onSave = handleSubmitRST(async (data) => {
    setIsSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await configMgmtControllerUpdateRequiredStudyType({
          requiredStudyTypeId: requiredStudyType?.id,
          requestBody: { name: data.name.trim(), code: data.code.trim(), status: data.status },
        });
      } else {
        await configMgmtControllerCreateRequiredStudyType({
          requestBody: {
            name: data.name.trim(),
            code: data.code.trim(),
            displayOrder: 0,
            status: data.status,
          },
        });
      }
      qc.invalidateQueries({ queryKey: ['globalRequiredStudyTypes'] });
      onClose();
    } catch (err: unknown) {
      try {
        const body = (await (err as { response?: Response }).response?.json()) as
          | { message?: string }
          | undefined;
        setError(body?.message ?? `Failed to ${isEdit ? 'update' : 'create'} required study`);
      } catch {
        setError(`Failed to ${isEdit ? 'update' : 'create'} required study`);
      }
    } finally {
      setIsSaving(false);
    }
  });

  const canSave = !!watchRST('name')?.trim() && !!watchRST('code')?.trim();

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isSaving) onClose();
      }}
    >
      <DialogContent className="max-w-md p-0 gap-0 sm:rounded-2xl overflow-hidden">
        <div className="border-b border-gray-100 px-6 py-5 pr-14">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? 'Edit Required Study' : 'New Required Study'}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            Maps the Tahseel "Required Study" code to a display name.
          </p>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <FormField label="Name" required>
                <Input
                  type="text"
                  autoFocus
                  placeholder="e.g. Annual Study"
                  {...registerRST('name', {
                    onBlur: (e) => {
                      if (!isEdit && !watchRST('code')) {
                        setValueRST('code', generateStudyCode(e.target.value));
                      }
                    },
                  })}
                />
              </FormField>
            </div>
            <FormField label="Code" required>
              <Input
                type="text"
                placeholder="e.g. 1"
                className="font-mono uppercase"
                {...registerRST('code')}
              />
            </FormField>
            <FormField label="Status">
              <Controller
                name="status"
                control={controlRST}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="h-10 border-gray-300 rounded-xl text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">Active</SelectItem>
                      <SelectItem value="INACTIVE">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </FormField>
          </div>

          {error && (
            <div className="rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600">
              {error}
            </div>
          )}
        </div>
        <DialogFooter className="border-t border-gray-100 px-6 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="success" size="sm" onClick={onSave} disabled={isSaving || !canSave}>
            {isSaving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Required Study'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequiredStudyTypesSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { data: requiredStudyTypes = [], isLoading } = useQuery<RequiredStudyType[]>({
    queryKey: ['globalRequiredStudyTypes'],
    queryFn: () =>
      configMgmtControllerListRequiredStudyTypes() as unknown as Promise<RequiredStudyType[]>,
  });

  const {
    showCreateRequiredStudy: showCreate,
    editRequiredStudy: _editRequiredStudy,
    deleteConfirmRequiredStudy: deleteConfirm,
    setShowCreateRequiredStudy: setShowCreate,
    setEditRequiredStudy,
    setDeleteConfirmRequiredStudy: setDeleteConfirm,
  } = usePledgeStudyConfigStore();
  const editRequiredStudy = _editRequiredStudy as RequiredStudyType | null;

  const deleteMutation = useMutation({
    mutationFn: (requiredStudyTypeId: string) =>
      configMgmtControllerDeleteRequiredStudyType({ requiredStudyTypeId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['globalRequiredStudyTypes'] });
      setDeleteConfirm(null);
    },
  });

  const columns: DataTableColumn<RequiredStudyType>[] = [
    {
      key: 'code',
      header: 'Code',
      render: (s) => (
        <span className="rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-xs font-semibold text-gray-600">
          {s.code}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (s) => <span className="font-medium text-gray-900">{s.name}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (s) => (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
            s.status === 'ACTIVE'
              ? 'bg-success-50 text-success-700 border border-success-200'
              : 'bg-gray-100 text-gray-500 border border-gray-200',
          )}
        >
          {s.status === 'ACTIVE' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: '',
            render: (s: RequiredStudyType) =>
              deleteConfirm === s.id ? (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">Delete?</span>
                  <button
                    type="button"
                    onClick={() => deleteMutation.mutate(s.id)}
                    disabled={deleteMutation.isPending}
                    className="text-xs font-medium text-error-600"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(null)}
                    className="text-xs text-gray-500"
                  >
                    No
                  </button>
                </div>
              ) : (
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setEditRequiredStudy(s)}
                    className="rounded px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(s.id)}
                    className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ),
          } satisfies DataTableColumn<RequiredStudyType>,
        ]
      : []),
  ];

  return (
    <>
      <DataTable
        title="Required Study"
        description='Maps the Tahseel "RequiredStudy" code (e.g. from the inbound webhook) to a display name.'
        headerActions={
          canEdit ? (
            <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
              <Plus size={14} />
              New Required Study
            </Button>
          ) : null
        }
        columns={columns}
        data={isLoading ? [] : requiredStudyTypes}
        keyField="id"
        emptyMessage="No required study types defined"
      />
      {showCreate && <RequiredStudyTypeDialog onClose={() => setShowCreate(false)} />}
      {editRequiredStudy && (
        <RequiredStudyTypeDialog
          requiredStudyType={editRequiredStudy}
          onClose={() => setEditRequiredStudy(null)}
        />
      )}
    </>
  );
}

// ── Tahseel Study Request Types ───────────────────────────────────────────────
// Tahseel's own "StudyType" lookup (periodic vs emergency, etc.) — a
// different classification axis from the "Study Types" tab above (which
// configures EIA/ERA/AQMA fee components).

function TahseelStudyRequestTypeDialog({
  tahseelStudyRequestType,
  onClose,
}: {
  tahseelStudyRequestType?: TahseelStudyRequestType;
  onClose: () => void;
}) {
  const isEdit = !!tahseelStudyRequestType;
  const qc = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register: registerTSRT,
    handleSubmit: handleSubmitTSRT,
    watch: watchTSRT,
    setValue: setValueTSRT,
    control: controlTSRT,
  } = useForm<z.infer<typeof tahseelStudyRequestTypeSchema>>({
    resolver: zodResolver(tahseelStudyRequestTypeSchema),
    defaultValues: {
      name: tahseelStudyRequestType?.name ?? '',
      code: tahseelStudyRequestType?.code ?? '',
      status: tahseelStudyRequestType?.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
    },
  });

  const onSave = handleSubmitTSRT(async (data) => {
    setIsSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await configMgmtControllerUpdateTahseelStudyRequestType({
          tahseelStudyRequestTypeId: tahseelStudyRequestType?.id,
          requestBody: { name: data.name.trim(), code: data.code.trim(), status: data.status },
        });
      } else {
        await configMgmtControllerCreateTahseelStudyRequestType({
          requestBody: {
            name: data.name.trim(),
            code: data.code.trim(),
            displayOrder: 0,
            status: data.status,
          },
        });
      }
      qc.invalidateQueries({ queryKey: ['globalTahseelStudyRequestTypes'] });
      onClose();
    } catch (err: unknown) {
      try {
        const body = (await (err as { response?: Response }).response?.json()) as
          | { message?: string }
          | undefined;
        setError(body?.message ?? `Failed to ${isEdit ? 'update' : 'create'} study type`);
      } catch {
        setError(`Failed to ${isEdit ? 'update' : 'create'} study type`);
      }
    } finally {
      setIsSaving(false);
    }
  });

  const canSave = !!watchTSRT('name')?.trim() && !!watchTSRT('code')?.trim();

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isSaving) onClose();
      }}
    >
      <DialogContent className="max-w-md p-0 gap-0 sm:rounded-2xl overflow-hidden">
        <div className="border-b border-gray-100 px-6 py-5 pr-14">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? 'Edit Study Type (Tahseel)' : 'New Study Type (Tahseel)'}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            Maps the Tahseel "StudyType" code to a display name (e.g. periodic vs emergency).
          </p>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <FormField label="Name" required>
                <Input
                  type="text"
                  autoFocus
                  placeholder="e.g. Request a periodic study"
                  {...registerTSRT('name', {
                    onBlur: (e) => {
                      if (!isEdit && !watchTSRT('code')) {
                        setValueTSRT('code', generateStudyCode(e.target.value));
                      }
                    },
                  })}
                />
              </FormField>
            </div>
            <FormField label="Code" required>
              <Input
                type="text"
                placeholder="e.g. 1"
                className="font-mono uppercase"
                {...registerTSRT('code')}
              />
            </FormField>
            <FormField label="Status">
              <Controller
                name="status"
                control={controlTSRT}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="h-10 border-gray-300 rounded-xl text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">Active</SelectItem>
                      <SelectItem value="INACTIVE">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </FormField>
          </div>

          {error && (
            <div className="rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600">
              {error}
            </div>
          )}
        </div>
        <DialogFooter className="border-t border-gray-100 px-6 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="success" size="sm" onClick={onSave} disabled={isSaving || !canSave}>
            {isSaving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Study Type'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TahseelStudyRequestTypesSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { data: tahseelStudyRequestTypes = [], isLoading } = useQuery<TahseelStudyRequestType[]>({
    queryKey: ['globalTahseelStudyRequestTypes'],
    queryFn: () =>
      configMgmtControllerListTahseelStudyRequestTypes() as unknown as Promise<
        TahseelStudyRequestType[]
      >,
  });

  const {
    showCreateTahseelStudyType: showCreate,
    editTahseelStudyType: _editTahseelStudyType,
    deleteConfirmTahseelStudyType: deleteConfirm,
    setShowCreateTahseelStudyType: setShowCreate,
    setEditTahseelStudyType,
    setDeleteConfirmTahseelStudyType: setDeleteConfirm,
  } = usePledgeStudyConfigStore();
  const editTahseelStudyType = _editTahseelStudyType as TahseelStudyRequestType | null;

  const deleteMutation = useMutation({
    mutationFn: (tahseelStudyRequestTypeId: string) =>
      configMgmtControllerDeleteTahseelStudyRequestType({ tahseelStudyRequestTypeId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['globalTahseelStudyRequestTypes'] });
      setDeleteConfirm(null);
    },
  });

  const columns: DataTableColumn<TahseelStudyRequestType>[] = [
    {
      key: 'code',
      header: 'Code',
      render: (s) => (
        <span className="rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-xs font-semibold text-gray-600">
          {s.code}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (s) => <span className="font-medium text-gray-900">{s.name}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (s) => (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
            s.status === 'ACTIVE'
              ? 'bg-success-50 text-success-700 border border-success-200'
              : 'bg-gray-100 text-gray-500 border border-gray-200',
          )}
        >
          {s.status === 'ACTIVE' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: '',
            render: (s: TahseelStudyRequestType) =>
              deleteConfirm === s.id ? (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">Delete?</span>
                  <button
                    type="button"
                    onClick={() => deleteMutation.mutate(s.id)}
                    disabled={deleteMutation.isPending}
                    className="text-xs font-medium text-error-600"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(null)}
                    className="text-xs text-gray-500"
                  >
                    No
                  </button>
                </div>
              ) : (
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setEditTahseelStudyType(s)}
                    className="rounded px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(s.id)}
                    className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ),
          } satisfies DataTableColumn<TahseelStudyRequestType>,
        ]
      : []),
  ];

  return (
    <>
      <DataTable
        title="Study Type (Tahseel)"
        description='Maps the Tahseel "StudyType" code (e.g. periodic vs emergency) to a display name. Unrelated to the "Study Types" tab, which configures EIA/ERA/AQMA fee categories.'
        headerActions={
          canEdit ? (
            <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
              <Plus size={14} />
              New Study Type
            </Button>
          ) : null
        }
        columns={columns}
        data={isLoading ? [] : tahseelStudyRequestTypes}
        keyField="id"
        emptyMessage="No Tahseel study types defined"
      />
      {showCreate && <TahseelStudyRequestTypeDialog onClose={() => setShowCreate(false)} />}
      {editTahseelStudyType && (
        <TahseelStudyRequestTypeDialog
          tahseelStudyRequestType={editTahseelStudyType}
          onClose={() => setEditTahseelStudyType(null)}
        />
      )}
    </>
  );
}

// ── Measurement Points ────────────────────────────────────────────────────────

function MeasurementPointDialog({
  point,
  onClose,
}: {
  point?: MeasurementPointType;
  onClose: () => void;
}) {
  const isEdit = !!point;
  const qc = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register: registerMP,
    handleSubmit: handleSubmitMP,
    watch: watchMP,
    control: controlMP,
  } = useForm<z.infer<typeof measurementPointSchema>>({
    resolver: zodResolver(measurementPointSchema),
    defaultValues: {
      name: point?.name ?? '',
      code: point?.code ?? '',
      unitPrice: point?.unitPrice ?? '',
      status: (point?.status as 'ACTIVE' | 'INACTIVE') ?? 'ACTIVE',
    },
  });

  const onSave = handleSubmitMP(async (data) => {
    setIsSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await pledgeStudyConfigControllerUpdateMeasurementPoint({
          id: point?.id,
          requestBody: { unitPrice: data.unitPrice, status: data.status },
        });
      } else {
        await pledgeStudyConfigControllerCreateMeasurementPoint({
          requestBody: {
            name: data.name.trim(),
            code: data.code.trim().toUpperCase(),
            unitPrice: data.unitPrice,
          },
        });
      }
      qc.invalidateQueries({ queryKey: ['measurementPoints'] });
      onClose();
    } catch {
      setError(`Failed to ${isEdit ? 'update' : 'create'} measurement point`);
    } finally {
      setIsSaving(false);
    }
  });

  const canSave = isEdit
    ? !!watchMP('unitPrice') && Number(watchMP('unitPrice')) > 0
    : !!watchMP('name')?.trim() &&
      !!watchMP('code')?.trim() &&
      !!watchMP('unitPrice') &&
      Number(watchMP('unitPrice')) > 0;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isSaving) onClose();
      }}
    >
      <DialogContent className="max-w-md p-0 gap-0 sm:rounded-2xl overflow-hidden">
        <div className="border-b border-gray-100 px-6 py-5 pr-14">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? 'Edit Measurement Point' : 'Add Measurement Point'}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            {isEdit
              ? 'Update the unit price or status for this measurement point'
              : 'Define a new measurement point with its unit price'}
          </p>
        </div>
        <div className="px-6 py-5 space-y-4">
          {isEdit ? (
            <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{point?.name}</p>
                <span className="inline-block rounded border border-gray-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-gray-500 mt-0.5">
                  {point?.code}
                </span>
              </div>
              {point?.previousUnitPrice && (
                <span className="text-xs text-gray-400 line-through">
                  <Price value={point?.previousUnitPrice} />
                </span>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <FormField label="Name" required>
                  <Input
                    type="text"
                    autoFocus
                    placeholder="e.g. Ground Floor, Mezzanine"
                    {...registerMP('name')}
                  />
                </FormField>
              </div>
              <FormField label="Code" required>
                <Input
                  type="text"
                  placeholder="e.g. GF, MEZ"
                  className="font-mono uppercase"
                  {...registerMP('code', {
                    onChange: (e) => {
                      e.target.value = e.target.value.toUpperCase();
                    },
                  })}
                />
              </FormField>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className={isEdit ? '' : 'col-span-2'}>
              <FormField label="Unit Price" required>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  autoFocus={isEdit}
                  {...registerMP('unitPrice')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canSave) onSave();
                  }}
                />
              </FormField>
            </div>
            {isEdit && (
              <FormField label="Status">
                <Controller
                  name="status"
                  control={controlMP}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="h-10 border-gray-300 rounded-xl text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="INACTIVE">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </FormField>
            )}
          </div>

          {error && (
            <div className="rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600">
              {error}
            </div>
          )}
        </div>
        <DialogFooter className="border-t border-gray-100 px-6 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="success" size="sm" onClick={onSave} disabled={isSaving || !canSave}>
            {isSaving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Measurement Point'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MeasurementPointsSection({ canEdit }: { canEdit: boolean }) {
  const { data: points, isLoading } = useQuery({
    queryKey: ['measurementPoints'],
    queryFn: () =>
      pledgeStudyConfigControllerListMeasurementPoints() as unknown as Promise<
        MeasurementPointType[]
      >,
  });

  const { showAdd, editPoint: _editPoint, setShowAdd, setEditPoint } = usePledgeStudyConfigStore();
  const editPoint = _editPoint as MeasurementPointType | null;

  return (
    <>
      <SectionCard
        title="Measurement Points"
        subtitle="Unit pricing per measurement point used in pledge studies"
        headerAction={
          canEdit ? (
            <Button variant="success" size="sm" onClick={() => setShowAdd(true)}>
              <Plus size={14} />
              Add Measurement
            </Button>
          ) : null
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <Th>Name</Th>
                <Th>Code</Th>
                <Th align="right">Unit Price</Th>
                <Th align="center">Status</Th>
                {canEdit && <Th>{''}</Th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <tr key={i}>
                      <Td>
                        <Skeleton className="h-4 w-32" />
                      </Td>
                      <Td>
                        <Skeleton className="h-5 w-16 rounded-md" />
                      </Td>
                      <Td align="right">
                        <Skeleton className="h-4 w-20 ml-auto" />
                      </Td>
                      <Td align="center">
                        <Skeleton className="h-6 w-16 mx-auto rounded-full" />
                      </Td>
                      {canEdit && <Td>{''}</Td>}
                    </tr>
                  ))
                : (points ?? []).map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                      <Td>
                        <span className="font-medium text-gray-900">{p.name}</span>
                      </Td>
                      <Td>
                        <span className="rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-xs font-semibold text-gray-500">
                          {p.code}
                        </span>
                      </Td>
                      <Td align="right">
                        <div className="flex items-center justify-end gap-2">
                          {p.previousUnitPrice && (
                            <span className="text-gray-300 line-through text-xs">
                              <Price value={p.previousUnitPrice} />
                            </span>
                          )}
                          <span className="text-xs text-gray-700">
                            <Price value={p.unitPrice} />
                          </span>
                        </div>
                      </Td>
                      <Td align="center">
                        <StatusBadge status={p.status} />
                      </Td>
                      {canEdit && (
                        <Td>
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => setEditPoint(p)}
                              className="rounded px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                            >
                              Edit
                            </button>
                          </div>
                        </Td>
                      )}
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {showAdd && <MeasurementPointDialog onClose={() => setShowAdd(false)} />}
      {editPoint && <MeasurementPointDialog point={editPoint} onClose={() => setEditPoint(null)} />}
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

type Tab = 'study-types' | 'required-study' | 'tahseel-study-type' | 'measurement-points';

// Study Types & Measurement Points feed Kalba's Pledge permit fee config; Required Study
// & Study Type (Tahseel) are Sharjah's Tahseel inbound webhook lookups.
const TAB_CONFIG: { id: Tab; label: string; viewPerm: string; editPerm: string }[] = [
  {
    id: 'study-types',
    label: 'Study Types',
    viewPerm: 'pledge-config:view',
    editPerm: 'pledge-config:edit',
  },
  {
    id: 'required-study',
    label: 'Required Study',
    viewPerm: 'sm-pledge-config:view',
    editPerm: 'sm-pledge-config:edit',
  },
  {
    id: 'tahseel-study-type',
    label: 'Study Type (Tahseel)',
    viewPerm: 'sm-pledge-config:view',
    editPerm: 'sm-pledge-config:edit',
  },
  {
    id: 'measurement-points',
    label: 'Measurement Points',
    viewPerm: 'pledge-config:view',
    editPerm: 'pledge-config:edit',
  },
];

function PledgeStudyConfigPage() {
  const { can } = usePermissions();
  const { activeTab, setActiveTab } = usePledgeStudyConfigStore();

  const visibleTabs = TAB_CONFIG.filter((tab) => can(tab.viewPerm));
  const currentTab = visibleTabs.find((tab) => tab.id === activeTab) ?? visibleTabs[0];

  return (
    <AppLayout>
      <PageHeader
        title="Pledge Studies"
        description="Configure pricing for study types and measurement points used in pledge permits."
        actions={
          <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm">
            <BookOpen size={15} className="text-gray-400" />
            <span className="text-xs text-gray-500">Pledge &amp; Study Configuration</span>
          </div>
        }
      />

      {/* Tab navigation */}
      <div className="flex gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1 w-fit">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'rounded-lg px-4 py-2 text-sm font-medium transition-all',
              currentTab?.id === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {currentTab?.id === 'study-types' && (
          <StudyTypesSection canEdit={can('pledge-config:edit')} />
        )}
        {currentTab?.id === 'required-study' && (
          <RequiredStudyTypesSection canEdit={can('sm-pledge-config:edit')} />
        )}
        {currentTab?.id === 'tahseel-study-type' && (
          <TahseelStudyRequestTypesSection canEdit={can('sm-pledge-config:edit')} />
        )}
        {currentTab?.id === 'measurement-points' && (
          <MeasurementPointsSection canEdit={can('pledge-config:edit')} />
        )}
      </div>
    </AppLayout>
  );
}
